/**
 * KgHeader · Top-of-page header block for the Knowledge Graph page.
 *
 * Byte-verbatim extraction of the Header render region that used to live at
 * lines 2883-3109 of /app/frontend/src/pages/KnowledgeGraphPage.jsx. Zero
 * visual change · every data-testid preserved · every className and inline
 * style preserved.
 *
 * Extracted 2026-08-01 · IJ-243 · Phase 5B of the KnowledgeGraphPage.jsx split
 * (see /app/memory/design_drafts/kg_page_split_map.md Section Three Boundary One).
 * Umbrella: IJ-076 Predictive Text Rebuild continuation · Refactor Programme 2026.
 * Owner: E1.
 *
 * Contents:
 *   - Search.Inc brand logo with the 'Actually not evil' hover tooltip
 *     (see useHeaderTooltip hook for the show/hide state).
 *   - Knowledge Atlas navigation icon with its hover-tag copy.
 *   - Knowledge Graph upload icon (admin-gated) with its hover-tag copy and
 *     shimmer sweep animation.
 *   - Username display plus the anonymous-username hover tag.
 *   - Exit button (non-blocking session-end trigger).
 *
 * Props:
 *   - isMobile · boolean · from useIsMobile in the parent.
 *   - hiddenTooltips · object · shared page-wide hidden-tooltips map (parent-owned).
 *   - onHideTooltip · function(id) · hide a tooltip · parent-owned.
 *   - onRestoreTooltip · function(id) · restore a hidden tooltip · parent-owned.
 *   - isAdmin · boolean · gates the upload icon.
 *   - kgShimmerKey · number · re-key value for the KG-icon shimmer animation.
 *   - displayUsername · string · rendered next to the user icon.
 *   - onExitClick · function() · Exit button click handler · parent-owned.
 *   - onOpenComparison · function() · called by the 'Compare us vs the rest'
 *     link · parent wraps setShowMenu(true)+setMenuView('comparison')+
 *     hideEvilTooltip() so this component stays unaware of the menu overlay.
 */
import React from 'react';
import { User } from 'lucide-react';
import { toast } from 'sonner';
import { RestoreTooltipDot } from './RestoreTooltipDot';
import { useHeaderTooltip } from '../../hooks/useHeaderTooltip';

export const KgHeader = ({
  isMobile,
  hiddenTooltips,
  onHideTooltip,
  onRestoreTooltip,
  isAdmin,
  kgShimmerKey,
  displayUsername,
  onExitClick,
  onOpenComparison,
}) => {
  const {
    showEvilTooltip,
    onLogoMouseEnter,
    onLogoMouseLeave,
    onTooltipMouseEnter,
    onTooltipMouseLeave,
    hideEvilTooltip,
  } = useHeaderTooltip(isMobile);

  const handleComparisonClick = () => {
    hideEvilTooltip();
    onOpenComparison();
  };

  const handleHideEvilClick = () => {
    onHideTooltip('evil');
    hideEvilTooltip();
  };

  return (
    <div className="bg-transparent relative z-30">
      <div className={`w-full ${isMobile ? 'px-3 py-2' : 'px-6 py-4'}`}>
        <div className="flex items-center justify-between">
          {/* Left: Search.Inc logo */}
          <div
            style={isMobile ? { marginTop: '12px', marginLeft: '8px' } : { marginTop: '38px', marginLeft: '76px' }}
            className="relative z-[500]"
            onMouseEnter={onLogoMouseEnter}
            onMouseLeave={onLogoMouseLeave}
          >
            {/* Logo image · IJ-175 · 2026-07-30 · new brand mark replaces the text wordmark plus tagline. The outer wrapping div preserves the 'Don't Be Evil' hover tooltip trigger. */}
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              <img
                src="/searchinc-logo.png"
                alt="Search.Inc — Actually not evil"
                style={{ height: isMobile ? 28 : 40 }}
                className="cursor-default select-none"
                draggable={false}
                data-testid="home-brand-logo"
              />
              {!isMobile && (
                <RestoreTooltipDot tooltipId="evil" hiddenTooltips={hiddenTooltips} onRestore={onRestoreTooltip} title="Bring back the 'Don't be Evil' hint" />
              )}
            </div>
            {/* Tooltip - hidden on mobile */}
            {showEvilTooltip && !isMobile && !hiddenTooltips['evil'] && (
              <div
                className="fixed z-[9999]"
                style={{ top: '38px', left: 'calc(76px + 290px)', marginLeft: '0' }}
                onMouseEnter={onTooltipMouseEnter}
                onMouseLeave={onTooltipMouseLeave}
              >
                <div className="px-5 py-4 bg-gray-800 text-lg text-white rounded leading-relaxed shadow-xl" style={{ width: '520px' }}>
                  One of Google's most important self-professed Core Values "Don't be Evil" was part of their brand values since it started. It was quietly removed in 2018. For Tru. Look it up (with us){' '}
                  <button
                    onClick={handleComparisonClick}
                    className="text-cyan-400 hover:text-cyan-300 underline cursor-pointer"
                    data-testid="compare-link"
                  >
                    Compare us vs the rest
                  </button>.
                  <div className="mt-2 border-t border-gray-700 pt-2">
                    <button onClick={handleHideEvilClick} className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors" data-testid="hide-tooltip-evil">Don't Show</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Upload/Knowledge Graph icons + Maverick */}
          <div className="flex items-center" style={isMobile ? { marginTop: '12px' } : {}}>
            {/* Knowledge Atlas icon — Placed FIRST (LEFT of KG upload icon) to
                mirror the KC page ordering per operator direction
                02-Jul-2026. Sits ~0.375 cm from the KG icon (same
                visual gap as the KC page — the isMobile branch
                keeps mr-3 for tap-target spacing on small screens). */}
            <div className={`flex items-center gap-1 ${isMobile ? 'mr-3' : ''}`} style={!isMobile ? { marginRight: '0.375cm' } : undefined}>
              <div className="relative group">
                <a
                  href="/knowledge-atlas"
                  className={`inline-flex p-1 transition-all ${isMobile ? '' : 'hover:scale-110'}`}
                  data-testid="atlas-nav-btn"
                  aria-label="Knowledge Atlas"
                  title="Knowledge Atlas"
                >
                  <img
                    src="/icons/wiki_256.png"
                    alt="Knowledge Atlas"
                    draggable={false}
                    className="select-none"
                    style={{
                      width: isMobile ? 28 : 39,
                      height: isMobile ? 28 : 39,
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.35))',
                    }}
                  />
                </a>
                {/* IJ-276e (2026-08-09) · restore-dot appears in the icon
                    corner when the Knowledge Atlas hint has been dismissed.
                    Mirrors the KG upload icon restore-dot pattern below. */}
                {hiddenTooltips['atlas_nav'] && !isMobile && (
                  <div className="absolute -top-1 -right-1 z-10">
                    <RestoreTooltipDot tooltipId="atlas_nav" hiddenTooltips={hiddenTooltips} onRestore={onRestoreTooltip} title="Bring back the Knowledge Atlas hint" />
                  </div>
                )}
                {!isMobile && !hiddenTooltips['atlas_nav'] && (
                  <div
                    className="absolute right-0 top-full pt-2 opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 pointer-events-none z-50"
                    style={{ width: '520px' }}
                    data-testid="atlas-nav-hover-tag"
                  >
                    {/* IJ-274 (2026-08-08) · pt-2 transparent bridge on
                        the outer wrapper replaces mt-2 gap so the mouse
                        stays inside .group when moving to the tip. */}
                    <div className="rounded-lg bg-gray-800 px-5 py-4 shadow-xl">
                      <p className="mb-2 text-xl font-bold text-white">Knowledge Atlas (KA)</p>
                      <p className="text-lg leading-relaxed text-gray-200">
                        Explore your personal knowledge in one intelligent
                        workspace.
                      </p>
                      <p className="mt-3 text-lg leading-relaxed text-gray-200">
                        Navigate connected topics, discover relationships and
                        instantly find the information you need &mdash; all
                        powered by your private Knowledge Graph.
                      </p>
                      <p className="mt-3 text-lg leading-relaxed text-gray-200">
                        Private. Searchable. Only accessible by you.
                      </p>
                      {/* IJ-276e (2026-08-09) · DON'T SHOW button restored.
                          Same styling as the KG upload and User-anonymity
                          hover tags on this header. */}
                      <button
                        onClick={(e) => { e.stopPropagation(); onHideTooltip('atlas_nav'); }}
                        className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
                        data-testid="hide-tooltip-atlas-nav"
                      >
                        Don&apos;t Show
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Upload icon */}
            <div className={`flex items-center gap-1 ${isMobile ? 'mr-3' : 'mr-10'}`}>
              <div className="relative group">
                <button
                  onClick={() => {
                    if (!isAdmin) {
                      toast.info('File upload is restricted to administrators.', { duration: 4500 });
                      return;
                    }
                    document.getElementById('file-input')?.click();
                  }}
                  className={`p-1 transition-all ${isAdmin ? 'hover:scale-110' : 'cursor-help'}`}
                  data-testid="upload-btn"
                  aria-label={isAdmin ? 'Knowledge Graph upload' : 'Knowledge Graph (admin upload only)'}
                >
                  <span
                    className="kg-icon-wrapper"
                    style={{
                      width: isMobile ? 28 : 39,
                      height: isMobile ? 28 : 39,
                    }}
                  >
                    <img
                      src="/icons/add-topic.png"
                      alt="Knowledge Graph"
                      className="kg-icon-img"
                      style={{
                        width: isMobile ? 28 : 39,
                        height: isMobile ? 28 : 39,
                        objectFit: 'contain',
                      }}
                    />
                    {/* Specular shimmer sweep — re-keyed on login or return-to-home to restart */}
                    <span key={kgShimmerKey} className="kg-icon-shimmer" aria-hidden="true" />
                  </span>
                </button>
                {/* Restore dot — appears in corner of icon when KG hint dismissed */}
                {hiddenTooltips['kg_upload'] && !isMobile && (
                  <div className="absolute -top-1 -right-1 z-10">
                    <RestoreTooltipDot tooltipId="kg_upload" hiddenTooltips={hiddenTooltips} onRestore={onRestoreTooltip} title="Bring back the Knowledge Graph hint" />
                  </div>
                )}
                {!isMobile && !hiddenTooltips['kg_upload'] && (
                  <div className="absolute right-0 top-full pt-2 opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50" style={{ width: '520px' }} data-testid="kg-upload-hover-tag">
                    <div className="p-5 bg-gray-800 rounded-lg shadow-xl">
                      <p className="text-xl text-white font-bold mb-2">Knowledge Graph (KG)</p>
                      <p className="text-lg text-gray-200 leading-relaxed">Upload artefacts to create a secure private library that powers smarter search, more accurate answers and advanced AI workflows.</p>
                      <p className="mt-3 text-lg text-gray-200 leading-relaxed">For Beta we&#39;ve preloaded the KG with AI and Search content so you can explore it immediately.</p>
                      <p className="mt-3 text-lg text-gray-200 leading-relaxed">Check out Atlas to see how it works.</p>
                      <button onClick={(e) => { e.stopPropagation(); onHideTooltip('kg_upload'); }} className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors" data-testid="hide-tooltip-kg-upload">Don&apos;t Show</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Legacy placement removed 02-Jul-2026 — Knowledge Atlas now sits
                LEFT of the KG upload icon (block above). */}
            <div className="flex items-start gap-2">
              {!isMobile && <span className="text-white text-lg font-medium mt-0.5" data-testid="user-display-name">{displayUsername || ''}</span>}
              <div className="flex flex-col items-center gap-1.5">
                <div className="relative group">
                  <div className="p-1 rounded-full border-2 border-cyan-400 text-cyan-400" data-testid="user-menu-btn">
                    <User className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
                  </div>
                  {/* Restore dot — appears on icon corner when this hint was dismissed */}
                  {hiddenTooltips['user_anonymity'] && !isMobile && (
                    <div className="absolute -top-1 -right-1 z-10">
                      <RestoreTooltipDot tooltipId="user_anonymity" hiddenTooltips={hiddenTooltips} onRestore={onRestoreTooltip} title="Bring back the Username privacy hint" />
                    </div>
                  )}
                  {/* Hover tag — privacy reassurance about Username anonymity.
                      Standard hover-tag treatment: same 380px width / p-4 size
                      as the Knowledge Graph hint, with a Don't Show button
                      that's restorable via the corner dot. */}
                  {!isMobile && !hiddenTooltips['user_anonymity'] && (
                    <div
                      className="absolute right-0 top-full pt-2 opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto translate-y-1 group-hover:translate-y-0 transition-all duration-200 pointer-events-none z-50"
                      style={{ width: '520px' }}
                      data-testid="user-icon-hover-tag"
                    >
                      <div className="p-5 bg-gray-800 rounded-lg shadow-xl">
                        <p className="text-xl text-white font-bold mb-2">Your Username is anonymous</p>
                        <p className="text-lg text-gray-200 leading-relaxed">
                          Your Username and who you actually are can never be linked in our system.
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); onHideTooltip('user_anonymity'); }}
                          className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
                          data-testid="hide-tooltip-user-anonymity"
                        >
                          Don&apos;t Show
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Exit button — non-blocking session-end trigger. Sits directly
                    under the user icon (vertical stack) so it visually belongs
                    to the user-identity cluster without crowding the username
                    horizontally. For non-admins it fires the leave-early banner
                    (or no-op if the completion banner is already showing). For
                    admin users it's a direct logout. */}
                <button
                  onClick={onExitClick}
                  className={`transition-colors rounded-md border border-cyan-500/50 hover:border-cyan-400 hover:bg-cyan-500/10 text-cyan-200 hover:text-cyan-50 font-medium ${isMobile ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-[10px] uppercase tracking-wider'}`}
                  data-testid="exit-btn"
                  aria-label="Exit Search.Inc"
                  title="Exit"
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KgHeader;
